import { Logo } from '@common/components/brand/Logo';
import type { Role } from '@features/auth';
import { useAuth } from '@features/auth';
import { Sidebar, useSidebar } from '@ntconsult/aluma-react';
import {
  Award,
  BookOpen,
  Home,
  LayoutDashboard,
  ListChecks,
  PenSquare,
  Route as RouteIcon,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useMatch } from 'react-router-dom';

type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
  allowedRoles?: Role[];
};

function NavItemLink({ item }: { item: NavItem }) {
  const match = useMatch({ path: item.to, end: false });
  const { isMobile, setOpen } = useSidebar();
  return (
    <Sidebar.Item asChild icon={item.icon} active={!!match}>
      {/* Em mobile a sidebar é overlay: fecha ao navegar para não tapar a página. */}
      <NavLink to={item.to} onClick={() => isMobile && setOpen(false)}>
        {item.label}
      </NavLink>
    </Sidebar.Item>
  );
}

function SidebarBrand() {
  const { t } = useTranslation('common');
  const { open, collapsible } = useSidebar();
  const collapsed = collapsible !== 'none' && !open;
  return (
    <Sidebar.Header className={collapsed ? 'justify-center' : undefined}>
      <Logo iconOnly size="md" />
      {!collapsed && <span className="text-base font-semibold">{t('appName')}</span>}
    </Sidebar.Header>
  );
}

/** Versão do app no rodapé da sidebar — sutil; some quando colapsada. */
function SidebarVersion() {
  const { open, collapsible } = useSidebar();
  const collapsed = collapsible !== 'none' && !open;
  if (collapsed) return null;
  return (
    <Sidebar.Footer>
      <span className="text-xs text-foreground-subtle">v{__APP_VERSION__}</span>
    </Sidebar.Footer>
  );
}

export function AppSidebar() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const isAdmin = !!user?.roles.includes('admin');

  const items: NavItem[] = [
    { to: '/meu-progresso', label: t('nav.inicio'), icon: <Home /> },
    { to: '/catalogo/cursos', label: t('nav.catalogo'), icon: <BookOpen /> },
    { to: '/meus-certificados', label: t('nav.meusCertificados'), icon: <Award /> },
    {
      to: '/autoria/cursos',
      label: t('nav.autoria'),
      icon: <PenSquare />,
      allowedRoles: ['professor'],
    },
    {
      to: '/curadoria/trilhas',
      label: t('nav.curadoria'),
      icon: <RouteIcon />,
      allowedRoles: ['curador'],
    },
    {
      to: '/painel/professor',
      label: t('nav.painelProfessor'),
      icon: <LayoutDashboard />,
      allowedRoles: ['professor'],
    },
    {
      to: '/painel/curador',
      label: t('nav.painelCurador'),
      icon: <ListChecks />,
      allowedRoles: ['curador'],
    },
    {
      to: '/painel/admin',
      label: t('nav.visaoGlobal'),
      icon: <LayoutDashboard />,
      allowedRoles: ['admin'],
    },
    {
      to: '/admin/usuarios',
      label: t('nav.usuarios'),
      icon: <Users />,
      allowedRoles: ['admin'],
    },
  ];

  // Admin contorna o gate (espelha RolesGuard do back).
  const visible = items.filter(
    (i) =>
      !i.allowedRoles?.length || isAdmin || i.allowedRoles.some((r) => user?.roles.includes(r)),
  );

  return (
    <Sidebar ariaLabel={t('nav.sidebar')}>
      <SidebarBrand />
      <Sidebar.Nav>
        {visible.map((item) => (
          <NavItemLink key={item.to} item={item} />
        ))}
      </Sidebar.Nav>
      <SidebarVersion />
    </Sidebar>
  );
}
